/**
 * useRedline* — eDMS E3 (redline accept/reject review).
 *
 * GET  /api/v1/documents/{id}/redlines              → proposed changes vs current.
 * POST /api/v1/documents/{id}/redlines/{changeId}   → set one decision.
 * POST /api/v1/documents/{id}/redlines/bulk         → accept/reject all.
 *
 * Served by the mock-api via the prod-proxy 404-fallback; decisions are held in
 * memory per document at prototype fidelity.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type RedlineKind = 'ADDED' | 'REMOVED' | 'MODIFIED';
export type RedlineStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED';

export interface RedlineChange {
  change_id: string;
  kind: RedlineKind;
  section: string;
  old_text: string | null;
  new_text: string | null;
  author: string;
  status: RedlineStatus;
}
export interface RedlineResponse {
  document_id: string;
  items: RedlineChange[];
  summary: { total: number; pending: number; accepted: number; rejected: number };
}

export function useRedlineQuery(documentId: string | undefined): UseQueryResult<RedlineResponse> {
  return useQuery<RedlineResponse>({
    queryKey: ['documents', documentId ?? '', 'redlines'],
    queryFn: ({ signal }) =>
      apiFetch<RedlineResponse>(`/api/v1/documents/${encodeURIComponent(documentId!)}/redlines`, { signal }),
    enabled: !!documentId,
    staleTime: 10_000,
  });
}

export function useRedlineDecision(documentId: string | undefined): UseMutationResult<
  RedlineResponse,
  Error,
  { changeId: string; decision: RedlineStatus }
> {
  const qc = useQueryClient();
  return useMutation<RedlineResponse, Error, { changeId: string; decision: RedlineStatus }>({
    mutationFn: ({ changeId, decision }) =>
      apiFetch<RedlineResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/redlines/${encodeURIComponent(changeId)}`,
        { method: 'POST', body: { decision } },
      ),
    onSuccess: (data) => qc.setQueryData(['documents', documentId ?? '', 'redlines'], data),
  });
}

export function useRedlineBulk(documentId: string | undefined): UseMutationResult<
  RedlineResponse,
  Error,
  { decision: 'ACCEPTED' | 'REJECTED' }
> {
  const qc = useQueryClient();
  return useMutation<RedlineResponse, Error, { decision: 'ACCEPTED' | 'REJECTED' }>({
    mutationFn: ({ decision }) =>
      apiFetch<RedlineResponse>(
        `/api/v1/documents/${encodeURIComponent(documentId!)}/redlines/bulk`,
        { method: 'POST', body: { decision } },
      ),
    onSuccess: (data) => qc.setQueryData(['documents', documentId ?? '', 'redlines'], data),
  });
}
